/**
 * NxConsolaAgente -- la consola conversacional COMPARTIDA de los agentes.
 *
 * NX-CONSOLA-T1 (idea de Josse, ASSIGN #8529).
 *
 * EL PORQUE, que es lo que no hay que perder de vista: comparando las dos
 * pantallas, la de Argemiro INVITA A CHATEAR y la de Presupuestos INVITA A
 * NAVEGAR UN MENU. Preguntar no hay que aprenderlo; un menu si. Por eso el chat
 * va arriba y de protagonista.
 *
 * Pero las acciones NO se botan (matiz de Ares): quien llega nuevo no sabe QUE
 * preguntar. Bajan a CHIPS debajo del hilo -- el menu deja de ser el plato y
 * pasa a ser la carta de sugerencias.
 *
 * API:
 *   montarConsola({
 *     contenedor,                       // elemento donde vive
 *     agente:      { nombre, avatar },  // quien habla
 *     saludo,                           // HTML del primer mensaje (opcional)
 *     sugerencias: [{ texto, accion }], // chips; accion() corre al tocarlos
 *     placeholder,                      // texto del input
 *     onPregunta                        // async (texto, api) => void
 *   })
 *
 * El onPregunta recibe una api chiquita para responder sin conocer el DOM:
 *   api.responder(html)   pinta una burbuja del agente
 *   api.usuario(texto)    pinta una burbuja del usuario (ya lo hace solo al enviar)
 *   api.pensando(bool)    prende/apaga el estado de pensando
 *   api.chips(lista)      reemplaza los chips (para ofrecer el siguiente paso)
 *
 * Las 12 reglas del COORD #8413 vienen de fabrica: input de una linea 38px,
 * tokens 4/8/12/16/24/32, un solo acento, focus visible, hilo con scroll propio
 * y estado de pensando propio -- nunca un spinner de pagina.
 */

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../shared/texto.js';
export function montarConsola({
  contenedor,
  agente = { nombre: 'Tu asistente', avatar: '\u{1F9D1}‍\u{1F4BC}' },
  saludo = '',
  sugerencias = [],
  placeholder = 'Escribe tu pregunta...',
  onPregunta = null
} = {}) {
  if (!contenedor) return null;

  contenedor.innerHTML = `
    <section class="nx-consola">
      <header class="nx-consola-cab">
        <span class="nx-consola-avatar">${esc(agente.avatar || '\u{1F9D1}‍\u{1F4BC}')}</span>
        <span class="nx-consola-nombre"></span>
      </header>

      <div class="nx-consola-hilo" data-consola-hilo></div>

      <div class="nx-consola-chips" data-consola-chips></div>

      <!-- La barra REUTILIZA las clases de la vista de Argemiro (agent-input-form
           + agent-textarea), no copia sus medidas: es literalmente el mismo CSS.
           Asi las dos consolas se ven hermanas por construccion, y afinar una
           afina la otra. Lo pidio Josse y el bug demostro por que hacia falta:
           .btn-primary trae width:100% desde base.css, y como aqui el boton
           llevaba flex:none (no encoge) se quedaba con TODO el ancho -- medido:
           boton 760px, input 25px. El formulario de Argemiro ya lo pisaba con
           width:auto; al compartir clase, la consola hereda el arreglo. -->
      <div class="nx-consola-barra agent-input-form">
        <textarea class="form-input agent-textarea nx-consola-input" rows="2" data-consola-input placeholder="${esc(placeholder)}"></textarea>
        <button type="button" class="btn-primary btn-sm nx-consola-enviar" data-consola-enviar>Preguntar</button>
      </div>
    </section>`;

  // El nombre del agente es DATO, no plantilla.
  contenedor.querySelector('.nx-consola-nombre').textContent = agente.nombre || 'Tu asistente';

  const $hilo = contenedor.querySelector('[data-consola-hilo]');
  const $chips = contenedor.querySelector('[data-consola-chips]');
  const $input = contenedor.querySelector('[data-consola-input]');
  const $enviar = contenedor.querySelector('[data-consola-enviar]');
  const textoEnviar = $enviar.textContent;

  const alFinal = () => { $hilo.scrollTop = $hilo.scrollHeight; };

  const burbujaAgente = (html) =>
    `<div class="nx-burbuja nx-burbuja-agente">
       <span class="nx-burbuja-avatar">${esc(agente.avatar || '\u{1F9D1}‍\u{1F4BC}')}</span>
       <div class="nx-burbuja-texto">${html}</div>
     </div>`;

  const api = {
    responder(html) {
      $hilo.insertAdjacentHTML('beforeend', burbujaAgente(html));
      alFinal();
      return $hilo.lastElementChild;
    },
    usuario(texto) {
      $hilo.insertAdjacentHTML('beforeend',
        `<div class="nx-burbuja nx-burbuja-usuario"><div class="nx-burbuja-texto">${esc(texto)}</div></div>`);
      alFinal();
    },
    pensando(encendido) {
      const previo = $hilo.querySelector('[data-consola-pensando]');
      if (previo) previo.remove();
      if (!encendido) return;
      // Estado propio, no spinner de pagina: la conversacion sigue a la vista.
      $hilo.insertAdjacentHTML('beforeend',
        `<div data-consola-pensando>${burbujaAgente('<span class="nx-consola-puntos"><i></i><i></i><i></i></span>')}</div>`);
      alFinal();
    },
    chips(lista) { pintarChips(lista || []); }
  };

  function pintarChips(lista) {
    if (!lista.length) { $chips.innerHTML = ''; return; }
    $chips.innerHTML = lista
      .map((s, i) => `<button type="button" class="nx-chip" data-consola-chip="${i}"${s.id ? ` data-chip-id="${esc(s.id)}"` : ''}>${esc(s.texto)}</button>`)
      .join('');
    $chips.querySelectorAll('[data-consola-chip]').forEach(b => {
      b.addEventListener('click', () => {
        const s = lista[Number(b.dataset.consolaChip)];
        if (s && typeof s.accion === 'function') s.accion(api);
      });
    });
  }

  const preguntar = async () => {
    const texto = ($input.value || '').trim();
    if (!texto || typeof onPregunta !== 'function') return;
    $input.value = '';
    api.usuario(texto);
    api.pensando(true);
    $enviar.disabled = true;
    $input.disabled = true;
    try {
      await onPregunta(texto, api);
    } finally {
      api.pensando(false);
      $enviar.disabled = false;
      $input.disabled = false;
      $enviar.textContent = textoEnviar;
      $input.focus();
    }
  };

  $enviar.addEventListener('click', preguntar);
  $input.addEventListener('keydown', (e) => {
    // Igual que Argemiro (AgentListView): Enter envia, Shift+Enter salta linea.
    // Antes era un input de una linea y Enter a secas bastaba; con textarea,
    // sin la excepcion del Shift no habria forma de escribir dos renglones.
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); preguntar(); }
  });

  if (saludo) api.responder(saludo);
  pintarChips(sugerencias);

  return api;
}
